/**
 * Timezone rounding utility for CubeJS preaggregation alignment.
 *
 * CubeJS builds preaggregations per-timezone. Only a fixed set of timezones
 * are configured in CUBEJS_SCHEDULED_REFRESH_TIMEZONES. When a query is sent
 * with an unsupported timezone, CubeJS falls back to slow source queries.
 *
 * This module rounds any IANA timezone to the nearest supported one so that
 * queries always hit preaggregated data.
 */

/**
 * The timezones configured in CUBEJS_SCHEDULED_REFRESH_TIMEZONES.
 * Must be kept in sync with deployment-config cubejs.yaml.
 */
const SUPPORTED_TIMEZONES = [
  'Pacific/Auckland',
  'Australia/Sydney',
  'Europe/London',
  'Europe/Berlin',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
] as const;

type SupportedTimezone = (typeof SUPPORTED_TIMEZONES)[number];

/**
 * Type guard to check if a string is a supported timezone.
 */
function isSupportedTimezone(tz: string): tz is SupportedTimezone {
  return (SUPPORTED_TIMEZONES as readonly string[]).includes(tz);
}

/**
 * Get the current UTC offset in minutes for a given IANA timezone.
 * Returns the offset as minutes ahead of UTC (e.g. +12:00 = 720, -05:00 = -300).
 */
function getUtcOffsetMinutes(tz: string): number {
  const now = new Date();

  // Format a date in the target timezone to extract the offset
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    timeZoneName: 'shortOffset',
  });

  const parts = formatter.formatToParts(now);
  const offsetPart = parts.find((p) => p.type === 'timeZoneName')?.value;

  if (!offsetPart || offsetPart === 'GMT') return 0;

  // Parse "GMT+12", "GMT-5", "GMT+5:30" etc.
  const match = offsetPart.match(/^GMT([+-])(\d{1,2})(?::(\d{2}))?$/);
  if (!match) return 0;

  const sign = match[1] === '+' ? 1 : -1;
  const hours = parseInt(match[2], 10);
  const minutes = parseInt(match[3] || '0', 10);

  return sign * (hours * 60 + minutes);
}

/**
 * Round an IANA timezone to the nearest supported preaggregation timezone.
 *
 * If the timezone is already in the supported list, returns it as-is.
 * Otherwise picks the supported timezone with the closest UTC offset.
 */
export function roundTimezone(tz: string): SupportedTimezone {
  // Exact match — short circuit
  if (isSupportedTimezone(tz)) {
    return tz;
  }

  const inputOffset = getUtcOffsetMinutes(tz);

  let bestMatch: SupportedTimezone = SUPPORTED_TIMEZONES[0];
  let bestDiff = Infinity;

  for (const supported of SUPPORTED_TIMEZONES) {
    const supportedOffset = getUtcOffsetMinutes(supported);
    const diff = Math.abs(inputOffset - supportedOffset);

    if (diff < bestDiff) {
      bestDiff = diff;
      bestMatch = supported;
    }
  }

  return bestMatch;
}

// Module-level cache — timezone doesn't change mid-session
let _cached: string | null = null;

/**
 * Get the browser's timezone, rounded to the nearest supported preaggregation timezone.
 * Result is memoized at module level since the browser timezone is stable within a session.
 */
export function getRoundedBrowserTimezone(): string {
  if (_cached === null) {
    const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    _cached = roundTimezone(browserTz);
  }
  return _cached;
}
